import { getZonedDate } from '@/lib/util/zonedTime';
import { addMonths, isSameMonth, subMonths, endOfMonth, format } from 'date-fns';
import { useEffect, useState } from 'react';

interface UseArchiveSelectorOptions {
  startDate: Date;
  onChange?: (month: number, year: number) => void;
}

function splitMonthYear (date: Date) {
  return [ date.getMonth(), date.getFullYear() ];
}

interface SelectOption {
  label: string;
  value: number;
}

export function useArchiveSelector (options: UseArchiveSelectorOptions) {
  /*
   * State
   */

  const [ currentMonth, setCurrentMonth ] = useState(Number(getZonedDate().getMonth()));
  const [ currentYear, setCurrentYear ] = useState(Number(getZonedDate().getFullYear()));
  const currentDate = new Date(currentYear, currentMonth, 1);

  /*
   * Computed state
   */

  const hasReachedStart = (() => {
    const [ month, year ] = splitMonthYear(options.startDate);
    return currentMonth == month && currentYear == year;
  })();

  const hasReachedEnd = (() => {
    const [ month, year ] = splitMonthYear(getZonedDate());
    return currentMonth == month && currentYear == year;
  })();

  const isCurrentMonth = (() => {
    return (
      isSameMonth(currentDate, getZonedDate()) && 
      currentDate.getFullYear() == getZonedDate().getFullYear()
    );
  })();

  const daysInMonth = (() => {
    return endOfMonth(currentDate).getDate();
  })();

  const monthsOptions = (() => {
    let startMonth = 0;
    let endMonth = 11;

    // start year
    if (options.startDate.getFullYear() == currentYear) {
      startMonth = options.startDate.getMonth();
    }
    // end year
    else if (getZonedDate().getFullYear() == currentYear) {
      endMonth = getZonedDate().getMonth();
    }

    const monthOptions: SelectOption[] = [];
    for (let i = startMonth; i <= endMonth; i++) {
      monthOptions.push({
        label: format(new Date(0, i), 'MMMM'), // e.g., "January"
        value: i, // e.g., "02" for February
      });
    }
    return monthOptions;
  })();

  const yearsOptions = (() => {
    const yearsOptions: SelectOption[] = [];
    const startYear = options.startDate.getFullYear();
    const currentYear = getZonedDate().getFullYear();
    for (let i = startYear; i <= currentYear; i++) {
      yearsOptions.push({
        label: i.toString(),
        value: i,
      });
    }
    return yearsOptions;
  })();

  /*
   * Methods 
   */

  const updateDate = (month: number, year: number) => {
    setCurrentMonth(Number(month));
    setCurrentYear(Number(year));
    if (options.onChange) options.onChange(Number(month), Number(year));
  };

  const prevMonth = () => {
    const [ month, year ] = splitMonthYear(subMonths(currentDate, 1));
    updateDate(month, year);
  };

  const nextMonth = () => {
    const [ month, year ] = splitMonthYear(addMonths(currentDate, 1));
    updateDate(month, year);
  };

  const setMonth = (month: number) => {
    updateDate(month, currentYear);
  };

  const setYear = (year: number) => {
    let nextMonth = currentMonth;
    const startYear = options.startDate.getFullYear();
    const startMonth = options.startDate.getMonth();
    const endYear = getZonedDate().getFullYear();
    const endMonth = getZonedDate().getMonth();

    // start year
    if (startYear == year && nextMonth < startMonth) {
      nextMonth = startMonth;
    }
    // end year
    else if (endYear == year && nextMonth > endMonth) {
      nextMonth = endMonth;
    }

    updateDate(nextMonth, year);
  };

  const isBeforeStartDate = (itemDate: Date) => {
    return itemDate < options.startDate;
  };

  /*
   * Return value
   */

  return {
    currentMonth,
    currentYear,
    currentDate,
    hasReachedStart,
    hasReachedEnd,
    isCurrentMonth,
    daysInMonth,
    monthsOptions,
    yearsOptions,
    nextMonth,
    prevMonth,
    setMonth,
    setYear,
    isBeforeStartDate,
  };
}
